import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { asArray } from "@/lib/asArray";
import { PdfJsViewer } from "@/components/PdfJsViewer";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { API_URL, toErrMsg } from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import { fmtPlural } from "@/lib/format";
import { fmtDate } from "@/lib/dt";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import AILabel from "@/components/AILabel";
import { DocRefDuplicateBadges, DocRefDuplicatePanel } from "@/components/DocRefDuplicate";   // Batch 30 · M76
import { ReadAgainBar, ReadAgainButton, ReadAgainTick, useReadAgainSelection } from "@/components/DocReadAgain";   // Batch 31 · item 16 (M84)
import { toast } from "sonner";
import { Upload, FileText, Loader2, Link2, Trash2, Sparkles, ChevronDown, ChevronRight, LayoutGrid, List, Maximize2 } from "lucide-react";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const DOC_TYPES = ["auto", "sales_invoice", "purchase_invoice", "po", "contract", "boq", "grn", "delivery_note", "bank_statement", "confirmation", "subcontractor_certificate", "progress_certificate", "memo", "engagement_letter", "financial_statements", "expert_report"];   // Batch 25 · C4 / C8; Batch 29 · item 8: memo / internal document; Batch 32 · S6; Batch 34 · Z-4
// Round 22 · H2 — readable labels; fields irrelevant to the predicted type are hidden.
const TYPE_FIELDS = {
  sales_invoice: ["invoice_number", "invoice_date", "customer_name", "vendor_name", "seller_name", "buyer_name", "total_amount", "tax_amount", "currency", "due_date", "line_items"],
  purchase_invoice: ["invoice_number", "invoice_date", "vendor_name", "customer_name", "seller_name", "buyer_name", "total_amount", "tax_amount", "currency", "due_date", "po_reference", "line_items"],
  po: ["po_number", "po_date", "vendor_name", "total_amount", "currency", "line_items"],
  contract: ["contract_number", "contract_date", "start_date", "end_date", "counterparty", "parties", "total_amount", "currency"],
  boq: ["boq_number", "date", "project_name", "total_amount", "currency", "line_items"],
  grn: ["grn_number", "grn_date", "vendor_name", "po_reference", "line_items"],
  delivery_note: ["dn_number", "dn_date", "vendor_name", "customer_name", "po_reference", "line_items"],
  bank_statement: ["account_number", "bank_name", "statement_date", "period_start", "period_end", "opening_balance", "closing_balance", "currency"],
  confirmation: ["counterparty", "balance_per_client", "balance_confirmed", "as_at_date", "difference_note"],                                  // Batch 25 · C4
  subcontractor_certificate: ["certificate_no", "date", "project", "party", "gross_value", "retention", "net_value"],                       // Batch 25 · C8
  progress_certificate: ["certificate_no", "date", "project", "party", "gross_value", "retention", "net_value"],                            // Batch 25 · C8
  memo: ["memo_reference", "date", "from_party", "to_party", "subject", "summary"],                                                        // Batch 29 · item 8
  engagement_letter: ["letter_date", "entity_name", "addressee", "period_end", "framework", "fee", "has_signature"],                         // Batch 32 · S6
  financial_statements: ["entity_name", "period_end", "framework", "total_assets", "total_equity", "profit_for_period", "has_signature"],   // Batch 32 · S6
  expert_report: ["report_date", "expert_name", "subject", "valuation_date", "valuation_amount", "currency", "method", "key_assumptions", "has_signature"],   // Batch 34 · Z-4
};
// Batch 31 · item 31: an extracted date ("2025-12-29", or with a time) shows as DD-MMM-YYYY — only a whole ISO date, so a
// reference that merely starts with digits ("2025-12-01-A") stays as read.
const ISO_DAY = /^\d{4}-\d{2}-\d{2}(T[\d:.]+(Z|[+-]\d{2}:?\d{2})?)?$/;
export const isoDay = (v) => (typeof v === "string" && ISO_DAY.test(v.trim()) ? fmtDate(v.trim()) : String(v));
const HIDDEN_FIELDS = new Set(["predicted_type", "is_support_document", "classification_confidence", "primary_reference", "raw_text", "notes", "raw", "error", "error_code", "error_detail", "direction_check", "pages_read"]);   // Batch 34 · Z-3: pages_read has its own row
export function fieldRows(doc, t) {
  const f = doc?.extracted_fields || {};
  const type = f.predicted_type || doc?.predicted_type || doc?.doc_type || "";
  const fmt = (v) => {
    if (v === null || v === undefined || v === "" || v === "null") return "—";
    if (typeof v === "boolean") return v ? t("docFields.yes") : t("docFields.no");
    if (Array.isArray(v)) return v.length ? `${v.length} ${t("docFields.items")}` : "—";
    if (typeof v === "object") return Object.entries(v).filter(([, x]) => x != null && x !== "").map(([k, x]) => `${k}: ${isoDay(x)}`).join(" · ") || "—";
    return isoDay(v);
  };
  // Round 33 · A5 — failed extraction: one fixed message, never the model's reply.
  if (doc?.extraction_status === "failed") {
    return [{ key: "error", label: t("docFields.status") || "Status", value: extractionReasonText(doc, t) || t("docsExtras.extractionFailed") || "Extraction failed — retry" }];   // Batch 34 · Z-3: the reason
  }
  const done = doc?.extraction_status === "done";
  const rows = [
    { key: "predicted_type", label: t("docFields.predicted_type"), value: type ? (t(`docs.types.${type}`) !== `docs.types.${type}` ? t(`docs.types.${type}`) : type) : "—" },
    { key: "is_support_document", label: t("docFields.is_support_document"), value: fmt(f.is_support_document ?? doc?.is_support_document) },
  ];
  if (done) {
    rows.push({ key: "classification_confidence", label: t("docFields.classification_confidence"),
      value: (f.classification_confidence ?? doc?.classification_confidence) != null ? `${Math.round(Number(f.classification_confidence ?? doc?.classification_confidence) * 100)}%` : "—" });
  }
  const allowed = TYPE_FIELDS[type];
  for (const [k, v] of Object.entries(f)) {
    if (HIDDEN_FIELDS.has(k)) continue;
    if (allowed && !allowed.includes(k) && (v === null || v === "" || v === undefined)) continue;
    const lk = `docFields.${k}`;
    const label = t(lk) !== lk ? t(lk) : k.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
    rows.push({ key: k, label, value: fmt(v) });
  }
  if (f.primary_reference) rows.push({ key: "primary_reference", label: t("docFields.primary_reference"), value: String(f.primary_reference) });
  if (pagesReadText(f, t)) rows.push({ key: "pages_read", label: t("docFields.pages_read"), value: pagesReadText(f, t) });   // Batch 34 · Z-3
  return rows;
}
import { LockedBanner } from "@/components/SignOffCard";
import DocumentReviewTabs from "@/components/DocumentReviewTabs";   // ND-1: tab 2 = the C-14 engagement-level documents section
import { askConfirm } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import { DOC_REVIEW_ACCEPT, splitDocReviewFiles } from "@/lib/docReviewFiles";   // Batch 32 · T7
import { extractionReasonText, pagesReadText } from "@/lib/i18n/1280_b34e";   // Batch 34 · Z-3
import { fmtNumber } from "@/components/Amount";   // STEP 3 · rule 2 · B-2e: the one amount formatter

export default function Documents() {
  const colW = useResizableColumns({ storageKey: "documents", defaults: [100, 110, 110, 110, 130], mins: [60, 72, 72, 72, 90] });   // Step 11d · add-on
  const { t, lang, dict } = useI18n();
  const { current } = useEngagement();
  const [items, setItems] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [dragActive, setDragActive] = useState(false);
  const [notAdded, setNotAdded] = useState(0);   // Batch 32 · T7
  const fileInputRef = useRef(null);
  // Batch 29 · M63: the type is detected automatically unless the auditor picks one (was "Purchase invoice")
  const [uploadForm, setUploadForm] = useState({ doc_type: "auto", vouching_item_id: "" });
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkTargetDoc, setLinkTargetDoc] = useState(null);
  const [linkSelected, setLinkSelected] = useState(new Set());
  const [savingLinks, setSavingLinks] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState("grouped"); // "grouped" | "flat"
  const [openGroups, setOpenGroups] = useState(new Set());
  // Batch 31 · item 16 (M84): documents ticked for "Read again" — none by default, no select-all
  // PR 45 review L8: cleared on an engagement switch (useReadAgainSelection)
  const { sel: raSel, toggle: raToggle, clear: raClear, replace: raReplace, setReplace: setRaReplace } = useReadAgainSelection(current?.id);
  const raDone = () => { raClear(); load(); };

  // Deep-link: when navigated from global search, scroll to and flash the doc.
  useEffect(() => {
    const hl = searchParams.get("highlight");
    if (!hl || documents.length === 0) return;
    const doc = documents.find((d) => d.id === hl);
    if (doc) setSelectedDoc(doc);
    const el = document.querySelector(`[data-testid="doc-item-${hl}"]`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("ring-2", "ring-emerald-500");
      setTimeout(() => el.classList.remove("ring-2", "ring-emerald-500"), 2400);
    }
    const next = new URLSearchParams(searchParams); next.delete("highlight");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line
  }, [documents, searchParams.toString()]);

  const openLinkDialog = (doc) => {
    const preselected = new Set([
      ...(doc.vouching_item_ids || []),
      ...(doc.vouching_item_id ? [doc.vouching_item_id] : []),
    ]);
    setLinkTargetDoc(doc);
    setLinkSelected(preselected);
    setLinkDialogOpen(true);
  };

  const toggleLinkItem = (id) => {
    setLinkSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const saveLinks = async () => {
    if (!linkTargetDoc) return;
    setSavingLinks(true);
    try {
      const ids = Array.from(linkSelected);
      const r = await api.post(`/documents/${linkTargetDoc.id}/link`, { vouching_item_ids: ids });
      toast.success(t("docsExtras.linkedToPlural").replace("{plural}", fmtPlural(ids.length, lang, dict.plurals.sampleLines)));
      await load();
      setSelectedDoc(r.data);
      setLinkDialogOpen(false);
    } catch (err) {
      toast.error(toErrMsg(err, t("docsExtras.linkFailed")));
    } finally {
      setSavingLinks(false);
    }
  };

  const deleteDoc = async (doc) => {
    try {
      await api.delete(`/documents/${doc.id}`);
      toast.success(t("docsExtras.docDeleted"));
      if (selectedDoc?.id === doc.id) setSelectedDoc(null);
      await load();
    } catch (err) {
      toast.error(toErrMsg(err, t("docsExtras.deleteFailed")));
    }
  };

  const engId = current?.id;
  const load = useCallback(async () => {
    if (!engId) return;
    const [v, d] = await Promise.all([
      api.get(`/vouching/${engId}`),
      api.get(`/documents/${engId}`),
    ]);
    setItems(v.data);
    setDocuments(d.data);
    // Batch 30 · M76: the open document's panel follows a change made from it (e.g. "Not a duplicate")
    setSelectedDoc((cur) => (cur ? (asArray(d.data).find((x) => x.id === cur.id) || cur) : cur));
  }, [engId]);

  useEffect(() => { load(); }, [load]);

  // Resolve each doc → set of Account Head buckets it belongs under. Docs
  // linked to items on multiple heads appear in each (audit reality). Unlinked
  // → "Unlinked / Unclassified".
  const itemById = useMemo(() => {
    const m = new Map();
    for (const it of items) m.set(it.id, it);
    return m;
  }, [items]);

  const groupedDocs = useMemo(() => {
    const groups = new Map(); // head → docs[]
    const UNLINKED = "Unlinked / Unclassified";
    for (const d of documents) {
      const linkedIds = Array.from(new Set([
        ...(d.vouching_item_ids || []),
        ...(d.vouching_item_id ? [d.vouching_item_id] : []),
      ]));
      const heads = new Set();
      for (const vid of linkedIds) {
        const it = itemById.get(vid);
        if (it && it.account_name) {
          heads.add(`${it.account_code || ""} · ${it.account_name}`.trim());
        }
      }
      if (heads.size === 0) heads.add(UNLINKED);
      for (const h of heads) {
        if (!groups.has(h)) groups.set(h, []);
        groups.get(h).push(d);
      }
    }
    // Sort — Unlinked first, then alpha
    const keys = Array.from(groups.keys()).sort((a, b) => {
      if (a === UNLINKED) return -1;
      if (b === UNLINKED) return 1;
      return a.localeCompare(b);
    });
    return keys.map((k) => ({ head: k, docs: groups.get(k) }));
  }, [documents, itemById]);

  // Auto-expand the Unlinked group + any group containing highlighted doc.
  useEffect(() => {
    if (openGroups.size > 0) return;
    const first = new Set(["Unlinked / Unclassified"]);
    setOpenGroups(first);
    // eslint-disable-next-line
  }, [groupedDocs.length]);

  const toggleGroup = (head) => {
    setOpenGroups((prev) => {
      const n = new Set(prev);
      if (n.has(head)) n.delete(head); else n.add(head);
      return n;
    });
  };
  const expandAll = () => setOpenGroups(new Set(groupedDocs.map((g) => g.head)));
  const collapseAll = () => setOpenGroups(new Set());

  const acceptPossibleMatch = async (doc, vouchingItemId) => {
    try {
      const r = await api.post(`/documents/${doc.id}/accept-possible-match`,
        { vouching_item_id: vouchingItemId });
      toast.success(t("docsExtras.linkedTick"));
      await load();
      setSelectedDoc(r.data);
    } catch (e) {
      toast.error(toErrMsg(e, t("docsExtras.acceptFailed")));
    }
  };
  const dismissPossibleMatches = async (doc) => {
    try {
      await api.post(`/documents/${doc.id}/dismiss-possible-matches`);
      await load();
    } catch (e) {
      toast.error(toErrMsg(e, t("docsExtras.dismissFailed")));
    }
  };

  useEffect(() => {
    if (!selectedDoc) { setPreviewUrl(null); return; }
    // Demo docs have no bytes → skip token mint.
    if (selectedDoc.storage_path?.includes("demo/")) { setPreviewUrl(null); return; }
    // Server designed path: mint a short-lived download-scoped token,
    // then hit /documents/file/{id}?mode=preview&token=… directly. The
    // preview mode sends Content-Disposition: inline + the exact
    // content-type, so Chrome/Edge/Safari render the PDF natively in
    // an <iframe> / <object>. The previous fetch-as-blob path was
    // wrapping an attachment-disposition response — Chromium refuses
    // to render blob-URL PDFs in <object> when the underlying response
    // was attachment-disposed, which is why the fallback "Your browser
    // blocked the inline PDF viewer" copy kept surfacing.
    // J3 — PDFs go through the built-in viewer which fetches with the auth header
    // (no token in the URL). Images still need a URL → short-lived scoped token.
    if (selectedDoc.content_type === "application/pdf") { setPreviewUrl(`/documents/file/${selectedDoc.id}?mode=preview`); return; }
    let cancel = false;
    api.post(`/documents/${selectedDoc.id}/download-token`)
      .then((r) => {
        if (cancel) return;
        const tok = r?.data?.token;
        if (!tok) return;
        setPreviewUrl(
          `${API_URL}/documents/file/${selectedDoc.id}?mode=preview&token=${encodeURIComponent(tok)}`
        );
      })
      .catch(() => {});
    return () => { cancel = true; };
    // eslint-disable-next-line
  }, [selectedDoc?.id]);

  const uploadOne = async (f, force = false) => {
    const fd = new FormData();
    fd.append("file", f);
    fd.append("engagement_id", current.id);
    fd.append("doc_type", uploadForm.doc_type);
    if (force) fd.append("force", "1");
    if (uploadForm.vouching_item_id && uploadForm.vouching_item_id !== "__none__") {
      fd.append("vouching_item_id", uploadForm.vouching_item_id);
    }
    try {
      return await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    } catch (e) {
      const d = e?.response?.data;
      if (e?.response?.status === 409 && d?.error_code === "DOCUMENT_DUPLICATE" && !force) {
        const ref = d?.detail?.duplicate_of || d?.duplicate_of || "";
        if (await askConfirm(t("docsExtras.duplicatePrompt").replace("{ref}", ref))) return uploadOne(f, true);
        return null;
      }
      throw e;
    }
  };

  // Multi-file upload. Runs a bounded pool so 20-file bulk drops don't
  // fan out unboundedly against the doc-extraction pipeline. Each file
  // gets its own /documents/upload → its own doc_id → its own AI
  // extraction job, so the caller (auditor) sees each show up in the
  // list with individual AI-linked voucher/party/amount fields.
  const uploadMany = async (fileList) => {
    // Batch 32 · T7: only PDF and images are sent; the rest is counted and named on the page
    const { accepted: files, rejected } = splitDocReviewFiles((fileList ? Array.from(fileList) : []).filter((f) => f && f.size > 0));   // Batch 25 · B2: a FileList is not an array — asArray would return []
    setNotAdded(rejected.length);
    if (!files.length || !current) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: files.length });
    let okCount = 0;
    let lastDoc = null;
    const CONCURRENCY = 3;
    let cursor = 0;
    const worker = async () => {
      while (cursor < files.length) {
        const idx = cursor++;
        const f = files[idx];
        try {
          const r = await uploadOne(f);
          if (!r) continue;
          okCount++;
          lastDoc = r.data;
        } catch (err) {
          toast.error(t("docsExtras.uploadFailedLine").replace("{name}", f.name).replace("{msg}", toErrMsg(err, t("docsExtras.uploadFailedSingle"))));
        }
        setUploadProgress((p) => ({ ...p, done: p.done + 1 }));
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, files.length) }, worker),
    );
    if (okCount > 0) {
      toast.success(
        files.length === 1
          ? t("docsExtras.uploadedSingle")
          : t("docsExtras.uploadedBatch").replace("{ok}", String(okCount)).replace("{total}", String(files.length)),
      );
    }
    await load();
    if (lastDoc) setSelectedDoc(lastDoc);
    setUploading(false);
    setUploadProgress({ done: 0, total: 0 });
    // Clear the picker so the same file can be re-picked next time.
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onUpload = (e) => uploadMany(e.target.files);

  const onDropZoneDragEnter = (e) => {
    if (uploading) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer?.types?.includes?.("Files")) setDragActive(true);
  };
  const onDropZoneDragOver = (e) => {
    if (uploading) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
  };
  const onDropZoneDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };
  const onDropZoneDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (uploading) return;
    const files = e.dataTransfer?.files;
    if (files && files.length) uploadMany(files);
  };

  if (!current) return <div className="text-sm text-muted-foreground">{t("common.noEng")}</div>;

  return (
    <div className="space-y-6" data-testid="documents-page">
      <LockedBanner engagement={current} />
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{t("docs.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("docs.subtitle")}</p>
      </div>

      {/* SUNDAY BATCH · ND-1: two sub-tabs — this page as it was (children) and "Engagement-level documents" (C-14 section) */}
      <DocumentReviewTabs engagement={current}>

      <Card>
        <CardHeader><CardTitle className="text-base">{t("docs.upload")}</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <Label>{t("docs.type")}</Label>
            <Select value={uploadForm.doc_type} onValueChange={(v) => setUploadForm({ ...uploadForm, doc_type: v })}>
              <SelectTrigger data-testid="upload-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DOC_TYPES.map((k) => <SelectItem key={k} value={k}>{t(`docs.types.${k}`)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>{t("docs.linkTo")}</Label>
            <Select value={uploadForm.vouching_item_id} onValueChange={(v) => setUploadForm({ ...uploadForm, vouching_item_id: v })}>
              <SelectTrigger data-testid="upload-link"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">—</SelectItem>
                {items.map((i) => (
                  <SelectItem key={i.id} value={i.id}>{i.voucher_no} · {i.account_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>&nbsp;</Label>
            <label
              onDragEnter={onDropZoneDragEnter}
              onDragOver={onDropZoneDragOver}
              onDragLeave={onDropZoneDragLeave}
              onDrop={onDropZoneDrop}
              className={`flex h-10 cursor-pointer items-center justify-center gap-2 rounded-md border px-4 text-sm font-medium text-white transition-all ${
                dragActive
                  ? "border-emerald-300 bg-emerald-600 shadow-lg ring-2 ring-emerald-400/60 scale-[1.02]"
                  : "border-transparent bg-slate-900 hover:bg-slate-800"
              } ${uploading ? "cursor-not-allowed opacity-70" : ""}`}
              data-testid="upload-dropzone"
              title={t("docsExtras.dropzoneTitle")}
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              <span data-testid="upload-button-label">
                {uploading
                  ? t("docsExtras.uploadingProgress").replace("{done}", String(uploadProgress.done)).replace("{total}", String(uploadProgress.total))
                  : dragActive
                    ? t("docsExtras.dropToUpload")
                    : t("docsExtras.uploadOrClick").replace("{upload}", t("docs.upload"))}
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept={DOC_REVIEW_ACCEPT}
                multiple
                className="hidden"
                onChange={onUpload}
                disabled={uploading}
                data-testid="upload-input"
              />
            </label>
            {notAdded > 0 && (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-300" data-testid="docs-not-added">
                {t("b33ui.filesNotAdded").replace("{n}", String(notAdded))}{/* Batch 33 · T7: + the related-party worksheet */}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-base">{t("docsExtras.docsCount").replace("{n}", String(documents.length))}</CardTitle>
            <div className="flex items-center gap-1">
              <div className="me-2 hidden items-center gap-1 rounded-md border p-0.5 md:flex"
                    data-testid="docs-view-toggle">
                <button type="button" onClick={() => setViewMode("grouped")}
                          title={t("docsExtras.groupedTitle")}
                          className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] ${
                            viewMode === "grouped"
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50"
                              : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                          data-testid="docs-view-grouped">
                  <LayoutGrid className="h-3 w-3" /> {t("docsExtras.groupedLabel")}
                </button>
                <button type="button" onClick={() => setViewMode("flat")}
                          title={t("docsExtras.flatTitle")}
                          className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] ${
                            viewMode === "flat"
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50"
                              : "text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"}`}
                          data-testid="docs-view-flat">
                  <List className="h-3 w-3" /> {t("docsExtras.flatLabel")}
                </button>
              </div>
              {selectedDoc && (
                <>
                  <ReadAgainButton doc={selectedDoc} replaceType={raReplace} lang={lang} onDone={raDone} />
                  <Button size="sm" variant="outline" onClick={() => openLinkDialog(selectedDoc)}
                          data-testid={`doc-link-btn-${selectedDoc.id}`}>
                    <Link2 className="me-1 h-3 w-3" /> {t("docs.manageLinks")}
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="ghost" data-testid={`doc-delete-btn-${selectedDoc.id}`}
                              className="text-red-600 hover:text-red-700">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("docs.delete")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("docs.deleteConfirm")}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteDoc(selectedDoc)}
                                            className="bg-red-600 hover:bg-red-700"
                                            data-testid={`doc-delete-confirm-${selectedDoc.id}`}>
                          {t("docs.delete")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0 max-h-[600px] overflow-y-auto">
            {documents.length > 0 && (
              <ReadAgainBar selectedIds={raSel} replaceType={raReplace} onReplaceType={setRaReplace} lang={lang} onDone={raDone} />
            )}
            {viewMode === "grouped" && documents.length > 0 && (
              <div className="flex items-center gap-2 border-b bg-slate-50/60 px-3 py-1.5 text-[10px] dark:bg-slate-900/40">
                <button type="button" onClick={expandAll}
                          className="text-emerald-700 hover:underline"
                          data-testid="docs-expand-all">{t("docsExtras.expandAll")}</button>
                <span className="text-slate-300">·</span>
                <button type="button" onClick={collapseAll}
                          className="text-emerald-700 hover:underline"
                          data-testid="docs-collapse-all">{t("docsExtras.collapseAll")}</button>
                <span className="ms-auto text-muted-foreground">
                  {fmtPlural(groupedDocs.length, lang, dict.plurals.groups)}
                </span>
              </div>
            )}

            {viewMode === "flat" && documents.map((d) => (
              <DocTile key={d.id} doc={d} items={items}
                          selected={selectedDoc?.id === d.id}
                          onSelect={() => setSelectedDoc(d)}
                          onAccept={acceptPossibleMatch}
                          onDismissHints={dismissPossibleMatches}
                          raChecked={raSel.has(d.id)} onRaToggle={raToggle}
                          onReload={load}
                          t={t} lang={lang} dict={dict} />
            ))}

            {viewMode === "grouped" && groupedDocs.map((g) => {
              const open = openGroups.has(g.head);
              const autoCount = g.docs.filter((x) => x.ai_auto_linked).length;
              const possibleCount = g.docs.filter((x) =>
                !x.ai_auto_linked && (x.ai_possible_matches || []).length > 0).length;
              return (
                <div key={g.head} data-testid={`doc-group-${g.head}`}>
                  <button type="button" onClick={() => toggleGroup(g.head)}
                            className="flex w-full items-center gap-2 border-b bg-slate-50/70 px-3 py-2 text-start text-xs font-medium hover:bg-slate-100 dark:bg-slate-900/40 dark:hover:bg-slate-900/60">
                    {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    <span className="min-w-0 flex-1 break-words text-slate-800 dark:text-slate-100">{g.head}</span>
                    <span className="rounded-md bg-white px-1.5 py-0.5 text-[10px] text-slate-600 dark:bg-slate-950 dark:text-slate-300">
                      {fmtPlural(g.docs.length, lang, dict.plurals.docs)}
                    </span>
                    {autoCount > 0 && (
                      <span className="rounded-md bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200"
                              title={t("docsExtras.autoLinkedCount").replace("{n}", String(autoCount))}>
                        ✨ {autoCount}
                      </span>
                    )}
                    {possibleCount > 0 && (
                      <span className="rounded-md bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-800"
                              title={t("docsExtras.possibleCount").replace("{n}", String(possibleCount))}>
                        ? {possibleCount}
                      </span>
                    )}
                  </button>
                  {open && g.docs.map((d) => (
                    <DocTile key={`${g.head}-${d.id}`} doc={d} items={items}
                              selected={selectedDoc?.id === d.id}
                              onSelect={() => setSelectedDoc(d)}
                              onAccept={acceptPossibleMatch}
                              onDismissHints={dismissPossibleMatches}
                              raChecked={raSel.has(d.id)} onRaToggle={raToggle}
                              onReload={load}
                              t={t} lang={lang} dict={dict} />
                  ))}
                </div>
              );
            })}

            {documents.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground">{t("docsExtras.noDocsYet")}</div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-1">
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-base">{t("docs.preview")}</CardTitle>
            {selectedDoc && previewUrl && (
              <button
                type="button"
                onClick={() => setZoomOpen(true)}
                title={t("docsExtras.zoomTitle")}
                data-testid="preview-zoom-btn"
                className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                <Maximize2 className="h-3.5 w-3.5" /> {t("docsExtras.zoomBtn")}
              </button>
            )}
          </CardHeader>
          <CardContent className="p-3">
            {!selectedDoc && <div className="p-6 text-center text-sm text-muted-foreground">{t("docsExtras.selectDoc")}</div>}
            {selectedDoc && (
              <div className="h-[560px] overflow-hidden rounded-md border bg-slate-50 dark:bg-slate-900">
                {previewUrl ? (
                  selectedDoc.content_type?.startsWith("image/") ? (
                    <img src={previewUrl} alt="" className="mx-auto h-full w-full object-contain" />
                  ) : selectedDoc.content_type === "application/pdf" ? (
                    <PdfJsViewer url={previewUrl} testId="doc-pdf-viewer" />
                  ) : (
                    <iframe src={previewUrl} title="doc"
                            sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                            className="h-full w-full" />
                  )
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    {selectedDoc.storage_path?.includes("demo/") ? t("docsExtras.demoDoc") : t("common.loading")}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-1">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CardTitle className="text-base">{t("docs.extracted")}</CardTitle>
              {selectedDoc && <AILabel />}
            </div>
          </CardHeader>
          <CardContent>
            {!selectedDoc && <div className="text-sm text-muted-foreground">—</div>}
            {selectedDoc && (
              <>
                <DocRefDuplicatePanel doc={selectedDoc} lang={lang} onChanged={load} />
                {((selectedDoc.vouching_item_ids || []).length > 0
                  || selectedDoc.vouching_item_id) && (
                  <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50/60 p-2 text-xs dark:bg-emerald-950/30 dark:border-emerald-900">
                    <div className="mb-1 text-[10px] uppercase tracking-wider text-muted-foreground">{t("docs.linkedTo")}</div>
                    <div className="flex flex-wrap gap-1">
                      {Array.from(new Set([...(selectedDoc.vouching_item_ids || []),
                                            ...(selectedDoc.vouching_item_id ? [selectedDoc.vouching_item_id] : [])])).map((vid) => {
                        const it = items.find((i) => i.id === vid);
                        return (
                          <span key={vid} className="rounded-md bg-emerald-100 px-2 py-0.5 font-mono text-[10px] text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-200">
                            {(it && (it.sample_item_uid || it.voucher_no)) || vid.slice(0, 8)}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
                <dl className="space-y-2 text-sm" data-testid="doc-extracted-fields">
                  {fieldRows(selectedDoc, t).map(({ key, label, value }) => (
                    <div key={key} className="flex flex-col gap-0.5 border-b pb-2 last:border-b-0" data-testid={`doc-field-${key}`}>
                      <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</dt>
                      <dd className="font-medium tabular-nums" dir="auto">{value}</dd>
                    </div>
                  ))}
                </dl>
              </>
            )}
          </CardContent>
        </Card>
      </div>
      </DocumentReviewTabs>

      {/* Link management dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t("docs.manageLinks")}</DialogTitle>
          </DialogHeader>
          <div className="text-xs text-muted-foreground">{t("docs.linkMulti")}</div>
          <div className="max-h-[400px] overflow-y-auto rounded-md border">
            <ResizableTable cols={colW} className="w-full text-sm">
              <thead className="border-b bg-slate-50 text-xs uppercase text-muted-foreground">
                <tr>
                  <ResizableTh index={0} className="w-10 px-3 py-2"></ResizableTh>
                  <ResizableTh index={1} className="px-3 py-2 text-start">{t("docsExtras.colUid")}</ResizableTh>
                  <ResizableTh index={2} className="px-3 py-2 text-start">{t("docsExtras.colVoucher")}</ResizableTh>
                  <ResizableTh index={3} className="px-3 py-2 text-start">{t("docsExtras.colAccount")}</ResizableTh>
                  <ResizableTh index={4} className="px-3 py-2 text-end">{t("docsExtras.colAmount")}</ResizableTh>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={5} className="px-3 py-4 text-center text-muted-foreground">{t("docsExtras.noSampleLines")}</td></tr>
                )}
                {items.map((it) => (
                  <tr key={it.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-900/50">
                    <td className="px-3 py-2 text-center">
                      <Checkbox
                        checked={linkSelected.has(it.id)}
                        onCheckedChange={() => toggleLinkItem(it.id)}
                        data-testid={`link-cb-${it.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 font-mono text-[10px] text-emerald-700">{it.sample_item_uid || "—"}</td>
                    <td className="px-3 py-2 font-medium">{it.voucher_no}</td>
                    <td className="px-3 py-2 text-xs">{it.account_name}</td>
                    <td className="px-3 py-2 num-cell">{fmtNumber(Number(it.amount || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </ResizableTable>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={saveLinks} disabled={savingLinks}
                    className="bg-slate-900 hover:bg-slate-800"
                    data-testid="link-save-btn">
              {savingLinks ? t("common.loading") : t("docsExtras.saveWithCount").replace("{n}", String(linkSelected.size))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Full-screen preview zoom — 90vw × 90vh Dialog. Reuses the
          same ?mode=preview&token=… URL so the browser's native PDF
          viewer handles zoom / pagination / text-selection for free. */}
      <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
        <DialogContent
          data-testid="preview-zoom-modal"
          className="max-w-[90vw] max-h-[90vh] w-[90vw] h-[90vh] p-0 gap-0 flex flex-col"
        >
          <DialogHeader className="border-b px-4 py-2 flex flex-row items-center justify-between gap-2 space-y-0">
            <DialogTitle className="text-sm font-medium break-words">
              {selectedDoc?.original_filename || t("docsExtras.docPreview")}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-slate-100 dark:bg-slate-900">
            {previewUrl && selectedDoc && (
              selectedDoc.content_type?.startsWith("image/") ? (
                <img src={previewUrl} alt="" className="h-full w-full object-contain" />
              ) : selectedDoc.content_type === "application/pdf" ? (
                <PdfJsViewer url={previewUrl} testId="preview-zoom-iframe" />
              ) : (
                <iframe
                  src={previewUrl}
                  title={t("docsExtras.docZoomAlt")}
                  className="h-full w-full border-0"
                  data-testid="preview-zoom-iframe"
                />
              )
            )}
          </div>
        </DialogContent>
      </Dialog>
      <div className="text-xs">
        <Link to="/doc-summaries" className="inline-flex items-center gap-1 text-sky-700 hover:underline" data-testid="docs-open-summaries-link">
          <Sparkles className="h-3.5 w-3.5" />{t("docsum.openInSummaries")}
        </Link>
      </div>
    </div>
  );
}



// ==============================================================
// Single document tile — used by both grouped + flat list. Adds:
//   • AI auto-linked chip (with confidence %)
//   • Possible-match hint list (50-74) with one-click Accept
// ==============================================================
// Step 11b · L9: `onReload` is the page's load() — DocTile is a top-level function, so it cannot see Documents()'s `load`
// (the Retry button called it directly: "load is not defined" after the read was queued, and the list never refreshed).
function DocTile({ doc, items, selected, onSelect, onAccept, onDismissHints, raChecked, onRaToggle, onReload, t, lang, dict }) {
  const linked = (doc.vouching_item_ids && doc.vouching_item_ids.length > 0)
                    || !!doc.vouching_item_id;
  const possible = !linked ? (doc.ai_possible_matches || []) : [];
  return (
    <div
      className={`border-b ${selected ? "bg-emerald-50 dark:bg-emerald-950/30" : "hover:bg-slate-50 dark:hover:bg-slate-900/50"}`}
      data-testid={`doc-item-${doc.id}`}>
      <div className="flex items-start">
      <span className="ps-3 pt-3"><ReadAgainTick doc={doc} checked={raChecked} onToggle={onRaToggle} lang={lang} /></span>
      <button
        type="button" onClick={onSelect}
        className="flex w-full items-start gap-3 p-3 text-start text-sm">
        <FileText className="mt-0.5 h-4 w-4 shrink-0 text-slate-500" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            {doc.doc_ref && (
              <span className="rounded-md bg-emerald-100 px-1.5 py-0.5 font-mono text-[10px] text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300"
                      data-testid={`doc-ref-${doc.id}`}>
                {doc.doc_ref}
              </span>
            )}
            <div className="font-medium break-words" title={doc.original_filename}>{doc.original_filename}</div>
            {doc.duplicate_of && (
              <span className="inline-flex items-center rounded-md bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold text-amber-800" data-testid={`doc-duplicate-${doc.id}`}>
                {t("docsExtras.duplicateBadge").replace("{ref}", doc.duplicate_of)}
              </span>
            )}
            <DocRefDuplicateBadges doc={doc} lang={lang} />
            {doc.ai_auto_linked && (
              <span className="ms-auto inline-flex items-center gap-1 rounded-md bg-emerald-100 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200"
                      title={t("docsExtras.aiConfidenceTitle").replace("{n}", String(Math.round(doc.ai_link_confidence || 0)))}
                      data-testid={`doc-auto-link-${doc.id}`}>
                <Sparkles className="h-2.5 w-2.5" />
                AI · {Math.round(doc.ai_link_confidence || 0)}%
              </span>
            )}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {(t(`docs.types.${doc.doc_type}`) !== `docs.types.${doc.doc_type}` ? t(`docs.types.${doc.doc_type}`) : (doc.doc_type || "").replace(/_/g, " ").replace(/^[a-z]/, (c) => c.toUpperCase()))}{" "}
            {doc.primary_reference && (
              <span className="ms-1 font-mono text-slate-700 dark:text-slate-300">
                · {doc.primary_reference}
              </span>
            )}
            {" · "}
            <span className={
              doc.extraction_status === "done" ? "text-emerald-600" :
              doc.extraction_status === "failed" ? "text-red-600" : "text-amber-600"
            }>
              {doc.extraction_status === "done" ? t("docs.done") :
                doc.extraction_status === "failed" ? (extractionReasonText(doc, t) || doc.extraction_error || t("docsExtras.extractionFailed") || "Not read — retry") : t("docs.pending")}
            </span>
            {doc.extraction_status === "failed" && (
              <button type="button" className="ms-1 rounded border px-1 text-[10px] text-sky-700 hover:bg-sky-50"
                      data-testid={`doc-retry-${doc.id}`}
                      onClick={async (ev) => { ev.stopPropagation(); try { await api.post(`/documents/${doc.id}/extract`); toast.success(t("docs.retryQueued")); if (onReload) await onReload(); } catch (e2) { toast.error(toErrMsg(e2, t("common.failed"))); } }}>
                {t("docs.retry")}
              </button>
            )}
            {(doc.is_support_document === false || doc.doc_type === "unclassified") && (
              <span className="ms-1 rounded-md bg-amber-100 px-1 py-0.5 text-[9px] text-amber-800"
                    title={t("docsExtras.unclassifiedWhy")}
                    data-testid={`doc-unclassified-${doc.id}`}>
                {t("docsExtras.unclassifiedBadge")}
              </span>
            )}
          </div>
        </div>
      </button>
      </div>
      <PermanentFileTick doc={doc} t={t} />   {/* Batch 10.1 · R2 — owner answer (c) */}
      {possible.length > 0 && (
        <div className="border-t border-amber-100 bg-amber-50/60 px-3 py-2 text-[11px] dark:border-amber-950 dark:bg-amber-950/30"
              data-testid={`doc-possible-${doc.id}`}>
          <div className="mb-1 flex items-center justify-between">
            <span className="font-medium text-amber-900 dark:text-amber-200">
              {t("docsExtras.possibleAiMatches")}
            </span>
            <button type="button" onClick={() => onDismissHints(doc)}
                     className="text-[10px] text-amber-800 hover:underline"
                     data-testid={`doc-possible-dismiss-${doc.id}`}>
              {t("docsExtras.dismissBtn")}
            </button>
          </div>
          <ul className="space-y-1">
            {possible.map((p) => (
              <li key={p.vouching_item_id}
                    className="flex items-center gap-2">
                <span className="font-mono text-[10px] text-emerald-700 dark:text-emerald-300">
                  {p.sample_item_uid || p.voucher_no}
                </span>
                <span className="break-words text-slate-700 dark:text-slate-200">
                  {p.account_name || "—"}
                </span>
                <span className="ms-auto tabular-nums text-slate-500">
                  {p.amount ? fmtNumber(Number(p.amount)) : "—"} · {Math.round(p.score)}%
                </span>
                <button type="button" onClick={() => onAccept(doc, p.vouching_item_id)}
                          className="rounded bg-emerald-600 px-2 py-0.5 text-[10px] font-medium text-white hover:bg-emerald-700"
                          data-testid={`doc-possible-accept-${doc.id}-${p.vouching_item_id}`}>
                  {t("docsExtras.acceptBtn")}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


// Batch 10.1 · R2 (owner answer (c)) — "Permanent file" tick: the Roll forward dialog lists the ticked documents and carries
// only those. Agreements, licences, constitution / CR, lease and loan agreements are SUGGESTED here, never ticked for you.
function PermanentFileTick({ doc, t }) {
  const [on, setOn] = useState(!!doc.permanent_file);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setOn(!!doc.permanent_file); }, [doc.permanent_file]);
  const toggle = async (v) => {
    setBusy(true);
    try {
      await api.patch(`/documents/${doc.id}/permanent-file`, { permanent_file: !!v });
      setOn(!!v);
      doc.permanent_file = !!v;
    } catch (e) {
      toast.error(toErrMsg(e, t("docsExtras.permanentFailed")));
    } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 px-3 pb-2 ps-10 text-[11px]" data-testid={`doc-permanent-row-${doc.id}`}>
      <label className="inline-flex items-center gap-1.5">
        <Checkbox checked={on} disabled={busy} onCheckedChange={toggle} data-testid={`doc-permanent-${doc.id}`} />
        <span>{t("docsExtras.permanentFile")}</span>
      </label>
      {!on && doc.suggested_permanent && (
        <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] text-sky-800 dark:bg-sky-950/40 dark:text-sky-200"
              data-testid={`doc-permanent-suggested-${doc.id}`}>{t("docsExtras.permanentSuggested")}</span>
      )}
    </div>
  );
}
